/**
 * JobTitleFormModal 컴포넌트
 *
 * 직책 마스터 생성/수정 모달입니다.
 *
 * **기능:**
 * - 직책명, 코드, 순서, 사용 여부, 단일 직책 여부, 설명 입력
 * - 생성 모드 / 수정 모드 지원
 * - 유효성 검사
 *
 * **사용 예시:**
 * ```tsx
 * <JobTitleFormModal
 *   isOpen={isModalOpen}
 *   jobTitleId={editingJobTitleId}
 *   onClose={() => setIsModalOpen(false)}
 *   onSuccess={() => refetch()}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { ModalOverlay } from '../../../shared/components/ModalOverlay';
import { useJobTitleMutations } from '../../../shared/hooks/useJobTitleMutations';
import { useJobTitle } from '../../../shared/hooks/useJobTitles';
import { CreateJobTitleInput, UpdateJobTitleInput } from '../../../shared/types/jobTitle';

/**
 * Props 타입 정의
 */
export interface JobTitleFormModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean;

  /** 수정 모드일 경우 직책 ID */
  jobTitleId?: string;

  /** 모달 닫기 콜백 */
  onClose: () => void;

  /** 저장 성공 콜백 */
  onSuccess: () => void;
}

/**
 * JobTitleFormModal 컴포넌트
 */
export function JobTitleFormModal({
  isOpen,
  jobTitleId,
  onClose,
  onSuccess,
}: JobTitleFormModalProps) {
  const { createJobTitle, updateJobTitle } = useJobTitleMutations();
  const { jobTitle, isLoading: isLoadingJobTitle } = useJobTitle(jobTitleId);

  const isEditMode = !!jobTitleId;

  // 폼 상태
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [order, setOrder] = useState<number>(0);
  const [status, setStatus] = useState<'active' | 'inactive'>('active');
  const [isUnique, setIsUnique] = useState(false);
  const [description, setDescription] = useState('');

  // 제출 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 수정 모드일 경우 기존 데이터 로드
   */
  useEffect(() => {
    if (isEditMode && jobTitle) {
      setName(jobTitle.name);
      setCode(jobTitle.code);
      setOrder(jobTitle.order);
      setStatus(jobTitle.status);
      setIsUnique(jobTitle.isUnique || false);
      setDescription(jobTitle.description || '');
    } else if (!isEditMode) {
      // 생성 모드일 경우 초기화
      setName('');
      setCode('');
      setOrder(0);
      setStatus('active');
      setIsUnique(false);
      setDescription('');
    }
  }, [isEditMode, jobTitle]);

  /**
   * 폼 초기화
   */
  const resetForm = () => {
    setName('');
    setCode('');
    setOrder(0);
    setStatus('active');
    setIsUnique(false);
    setDescription('');
    setError(null);
  };

  /**
   * 모달 닫기
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  /**
   * 저장 버튼 클릭
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    if (!name.trim()) {
      setError('직책명을 입력해주세요.');
      return;
    }

    if (!code.trim()) {
      setError('직책 코드를 입력해주세요.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isEditMode && jobTitleId) {
        // 수정 모드
        const input: UpdateJobTitleInput = {
          name: name.trim(),
          code: code.trim(),
          order,
          status,
          isUnique,
          description: description.trim(),
        };

        await updateJobTitle(jobTitleId, input);
        alert('직책이 수정되었습니다.');
      } else {
        // 생성 모드
        const input: CreateJobTitleInput = {
          name: name.trim(),
          code: code.trim(),
          order,
          status,
          isUnique,
          description: description.trim(),
        };

        await createJobTitle(input);
        alert('직책이 생성되었습니다.');
      }

      onSuccess();
      handleClose();
    } catch (err: any) {
      console.error('직책 저장 실패:', err);
      setError(err.message || '직책 저장에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClose={handleClose} style={styles.overlay}>
      <div style={styles.modal}>
        {/* 헤더 */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            {isEditMode ? '직책 수정' : '직책 추가'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.closeButton}
            disabled={isSubmitting}
          >
            <X size={24} />
          </button>
        </div>

        {/* 로딩 중 (수정 모드에서 데이터 로딩) */}
        {isEditMode && isLoadingJobTitle ? (
          <div style={styles.loading}>데이터를 불러오는 중...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* 폼 본문 */}
            <div style={styles.body}>
              {/* 에러 메시지 */}
              {error && <div style={styles.errorBox}>{error}</div>}

              {/* 직책명 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  직책명 <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="예: 팀장"
                  style={styles.input}
                  disabled={isSubmitting}
                  required
                />
              </div>

              {/* 직책 코드 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  직책 코드 <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="예: TEAM_LEADER"
                  style={styles.input}
                  disabled={isSubmitting}
                  required
                />
              </div>

              {/* 정렬 순서 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>
                  정렬 순서 <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="number"
                  value={order}
                  onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
                  placeholder="낮을수록 높은 직책 (대표=1, 팀원=6)"
                  style={styles.input}
                  disabled={isSubmitting}
                  required
                />
                <p style={styles.hint}>
                  낮을수록 높은 직책입니다 (대표=1, 본부장=2, ..., 팀원=6)
                </p>
              </div>

              {/* 사용 여부 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>사용 여부</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as 'active' | 'inactive')}
                  style={styles.select}
                  disabled={isSubmitting}
                >
                  <option value="active">사용</option>
                  <option value="inactive">미사용</option>
                </select>
              </div>

              {/* 단일 직책 여부 */}
              <div style={styles.formGroup}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={isUnique}
                    onChange={(e) => setIsUnique(e.target.checked)}
                    disabled={isSubmitting}
                    style={styles.checkbox}
                  />
                  <span>단일 직책 (1명만 보유 가능)</span>
                </label>
                <p style={styles.hint}>
                  대표, 담당 등 1명만 보유할 수 있는 직책일 경우 체크하세요.
                </p>
              </div>

              {/* 설명 */}
              <div style={styles.formGroup}>
                <label style={styles.label}>설명</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="직책에 대한 설명을 입력하세요"
                  style={styles.textarea}
                  disabled={isSubmitting}
                  rows={3}
                />
              </div>
            </div>

            {/* 푸터 (버튼) */}
            <div style={styles.footer}>
              <button
                type="button"
                onClick={handleClose}
                style={styles.cancelButton}
                disabled={isSubmitting}
              >
                취소
              </button>
              <button
                type="submit"
                style={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? '저장 중...' : isEditMode ? '수정' : '추가'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalOverlay>
  );
}

/**
 * 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-gray-600)',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: {
    padding: '24px',
    flex: 1,
    overflowY: 'auto',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: 'var(--color-gray-600)',
  },
  errorBox: {
    padding: '12px 16px',
    backgroundColor: '#fee',
    border: '1px solid #fcc',
    borderRadius: '6px',
    color: '#c33',
    fontSize: '14px',
    marginBottom: '16px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  select: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--color-gray-700)',
    cursor: 'pointer',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  hint: {
    fontSize: '12px',
    color: 'var(--color-gray-500)',
    marginTop: '6px',
    margin: 0,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: '1px solid var(--color-gray-200)',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};
